import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link, Navigate, useNavigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store';
import { useAppSelector, useAppDispatch } from './store/hooks';
import { branding } from './constants/branding';
import { FaHome, FaList, FaReceipt, FaChartPie, FaEllipsisH, FaBell, FaUserCircle, FaCog, FaCreditCard, FaPiggyBank, FaChartLine, FaBtc, FaBars, FaChevronLeft, FaUsers, FaShareAlt, FaComments } from 'react-icons/fa';
import Footer from './components/Footer';
import PageTransition from './components/PageTransition';
import './App.css';
import { logoutUser, checkTokenExpiry, initializeAuth, updateLastActivity, setAdminUser } from './store/slices/authSlice';
import { useSelector } from 'react-redux';
import { RootState } from './store';
import { fetchProfile } from './store/slices/profileSlice';
import { useAccessControl } from './utils/accessControl';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useWebSocketNotifications } from './hooks/useWebSocketNotifications';
import { authMiddleware } from './middleware/authMiddleware';
import { useContextAwarePersistence } from './hooks/useContextAwarePersistence';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';
import InactivityModal from './components/InactivityModal';
import TokenRefreshTest from './components/TokenRefreshTest';
import DebugPanel from './components/DebugPanel';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Cards from './pages/Cards';
import Crypto from './pages/Crypto';
import Assets from './pages/Assets';
import Savings from './pages/Savings';
import Batch from './pages/Batch';
import Settings from './pages/Settings';
import AdminSettings from './pages/AdminSettings';
import AdminTradingBots from './pages/AdminTradingBots';
import TradingBots from './pages/TradingBots';
import CryptoPortfolio from './pages/CryptoPortfolio';
import OrderBookPage from './pages/OrderBook';
import IGaming from './pages/IGaming';
import P2PGames from './pages/P2PGames';
import PendingChallenges from './pages/PendingChallenges';
import GameLobby from './pages/GameLobby';
import P2PGamePlayer from './pages/P2PGamePlayer';
import Friends from './pages/Friends';
import GamePlayer from './pages/GamePlayer';
import ProfitAnalytics from './pages/ProfitAnalytics';
import MultiSignatureWallets from './pages/MultiSignatureWallets';
import Stablecoin from './pages/Stablecoin';
import TrueLayerConnect from './pages/TrueLayerConnect';
import SocialConnect from './pages/SocialConnect';
import Transactions from './pages/Transactions';
import MakePayment from './pages/MakePayment';
import Profile from './pages/Profile';
import ChildWallet from './pages/ChildWallet';
import Account from './pages/Account';
import Payees from './pages/Payees';
import Register from './pages/Register';
import PersonalRegister from './pages/PersonalRegister';
import EnhancedPersonalRegister from './pages/EnhancedPersonalRegister';
import EnhancedBusinessRegister from './pages/EnhancedBusinessRegister';
import BusinessRegister from './pages/BusinessRegister';
import VerifyEmail from './pages/VerifyEmail';
import ConfirmLoginChallenge from './pages/ConfirmLoginChallenge';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import Notifications from './pages/Notifications';
import MT5Trading from './pages/MT5Trading';
import SocialMedia from './pages/SocialMedia';
import { AdminLayout } from './components/AdminLayout';
import CustomerManagement from './components/admin/CustomerManagement';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { adminAuthService } from './services/adminAuthService';
import { apiService } from './services/apiService';
import { useFeatureFlags } from './config/featureFlags';
import SubscriptionExpiredBanner from './components/SubscriptionExpiredBanner';
import SubscriptionExpiredScreen from './pages/SubscriptionExpiredScreen';
import MarketLanding from './pages/MarketLanding';
import FaqPage from './pages/FaqPage';
import CreatorChatWidgetPage from './pages/CreatorChatWidgetPage';
import RetailCreatorChatCatalog from './pages/RetailCreatorChatCatalog';
import RetailSubscriptionRenewalPage from './pages/RetailSubscriptionRenewalPage';
import AdminSubscriptionRenewalPage from './pages/AdminSubscriptionRenewalPage';
import AdminSubscriptionGate from './components/AdminSubscriptionGate';

/** Default header avatar when user has no `avatarBase64` (file: `public/assets/ProfileTemp.png`). */
const DEFAULT_HEADER_PROFILE_IMAGE = `${process.env.PUBLIC_URL || ''}/assets/ProfileTemp.png`;

// Dynamic greeting function
const getTimeBasedGreeting = (firstName?: string) => {
  const hour = new Date().getHours();
  let timeGreeting;
  
  if (hour < 12) {
    timeGreeting = 'Good morning';
  } else if (hour < 17) {
    timeGreeting = 'Good afternoon';
  } else {
    timeGreeting = 'Good evening';
  }
  
  const name = firstName || 'User';
  return `${timeGreeting}, ${name} 👋`;
};

const MOBILE_BREAKPOINT = 768;

// Token expiry check hook
const useTokenExpiryCheck = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
  const tokenExpiry = useAppSelector(state => state.auth.tokenExpiry);
  const isLoading = useAppSelector(state => state.auth.isLoading);
  const lastTokenCheck = useAppSelector(state => state.auth.lastTokenCheck);

  useEffect(() => {
    if (!isAuthenticated || !tokenExpiry || isLoading) return;

    const now = Date.now();
    
    // Don't check more than once every 2 minutes
    if (lastTokenCheck && (now - lastTokenCheck) < 120000) {
      console.log('Token check skipped - too recent');
      return;
    }

    // Only check if token is close to expiry (within 10 minutes)
    const expiryTime = new Date(tokenExpiry).getTime();
    const timeUntilExpiry = expiryTime - now;
    const tenMinutes = 10 * 60 * 1000; // 10 minutes in milliseconds

    // If token expires in more than 10 minutes, set a timeout to check later
    if (timeUntilExpiry > tenMinutes) {
      console.log(`Token expires in ${Math.round(timeUntilExpiry / 60000)} minutes, scheduling check for later`);
      const timeoutId = setTimeout(() => {
        dispatch(checkTokenExpiry());
      }, timeUntilExpiry - tenMinutes);

      return () => clearTimeout(timeoutId);
    }

    // If token is close to expiry, check now and then every 2 minutes (not every minute)
    console.log(`Token expires in ${Math.round(timeUntilExpiry / 60000)} minutes, starting regular checks`);
    
    // Initial check only if close to expiry and haven't checked recently
    if (!lastTokenCheck || (now - lastTokenCheck) >= 120000) {
      dispatch(checkTokenExpiry());
    }

    // Set up interval for regular checks (every 2 minutes instead of 1)
    const checkInterval = setInterval(() => {
      const currentTime = Date.now();
      const currentExpiryTime = new Date(tokenExpiry).getTime();
      const currentTimeUntilExpiry = currentExpiryTime - currentTime;
      
      // Only continue checking if still close to expiry
      if (currentTimeUntilExpiry <= tenMinutes) {
        dispatch(checkTokenExpiry());
      } else {
        clearInterval(checkInterval);
      }
    }, 120000); // Check every 2 minutes instead of 1

    return () => clearInterval(checkInterval);
  }, [isAuthenticated, tokenExpiry, isLoading, lastTokenCheck, dispatch]);
};

// Auth expired event listener hook
const useAuthExpiredListener = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    const handleAuthExpired = () => {
      const isAdminPath = window.location.pathname.startsWith('/admin');
      if (isAdminPath) {
        const urlParams = new URLSearchParams(window.location.search);
        const tabFromUrl = urlParams.get('tab');
        const tabFromStorage = localStorage.getItem('adminActiveTab');
        const currentTab = tabFromUrl || tabFromStorage;
        if (currentTab && currentTab !== 'dashboard') {
          sessionStorage.setItem('adminReturnTab', currentTab);
        }
        navigate('/admin/login', { replace: true });
        return;
      }
      navigate('/login', { replace: true });
    };

    window.addEventListener('auth-expired', handleAuthExpired);
    return () => window.removeEventListener('auth-expired', handleAuthExpired);
  }, [navigate]);
};

const isAdminPublicRoute = (pathname: string): boolean =>
  pathname === '/admin/login' ||
  pathname === '/admin/confirm-geolocation' ||
  pathname === '/admin/confirm-new-device' ||
  pathname === '/admin/subscription-renew' ||
  pathname === '/admin';

// --- MOBILE COMPONENTS ---
const BottomNavBar: React.FC<{ onMoreClick: () => void }> = ({ onMoreClick }) => {
    const location = useLocation();
    const { getFilteredNavigation, isChildAccount } = useAccessControl();
    
    // Get filtered navigation items based on permissions (same as desktop)
    const filteredNavigation = getFilteredNavigation();
    
    // Define icon mapping for bottom nav items
    const getBottomNavIcon = (path: string) => {
        const iconColor = '#086afb'; // Zogzila accent blue
        const iconMap: { [key: string]: React.JSX.Element } = {
            '/dashboard': <FaHome size={22} color={iconColor} />,
            '/transactions': <FaList size={22} color={iconColor} />,
            '/account': <FaUserCircle size={22} color={iconColor} />,
            '/payments': <FaReceipt size={22} color={iconColor} />,
        };
        return iconMap[path] || <FaUserCircle size={22} color={iconColor} />;
    };
    
    // Get base navigation items for mobile bottom bar - only show first 4 items that user has permission for
    // Priority order: Dashboard, Transactions, Account, Payments
    const priorityPaths = ['/dashboard', '/transactions', '/account', '/payments'];
    const availablePriorityItems = priorityPaths
        .map(path => {
            const navItem = filteredNavigation.find(item => item.path === path);
            return navItem ? { icon: getBottomNavIcon(path), label: navItem.label, path } : null;
        })
        .filter(item => item !== null) as Array<{ icon: React.JSX.Element; label: string; path: string }>;
    
    // Add "More" button if there are more items available or if we have fewer than 4 priority items
    const hasMoreItems = filteredNavigation.length > availablePriorityItems.length;
    const baseNavItems = [
      ...availablePriorityItems,
      ...(hasMoreItems ? [{ icon: <FaEllipsisH size={22} color="#086afb" />, label: 'More', path: undefined as any }] : [])
    ];
    
    // Filter navigation items based on account type (for child accounts, limit to allowed pages)
    const filteredNavItems = isChildAccount 
      ? baseNavItems.filter(item => 
          !item.path || ['/dashboard', '/transactions', '/account'].includes(item.path)
        )
      : baseNavItems;
  
    return (
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, height: 65,
        backgroundColor: '#ffffff', borderTop: '1px solid #e0e0e0',
        display: 'flex', justifyContent: 'space-around', alignItems: 'center',
        paddingBottom: 5, boxShadow: '0 -2px 10px rgba(0,0,0,0.05)', zIndex: 1000,
      }}>
        {filteredNavItems.map((item) => {
          if (!item.path) {
            return (
              <div key={item.label} onClick={onMoreClick} style={{ textAlign: 'center', color: '#6c757d', textDecoration: 'none', cursor: 'pointer', padding: '0 8px' }}>
                {item.icon}
                <div style={{ fontSize: 10, marginTop: 4 }}>{item.label}</div>
              </div>
            );
          }
          return (
            <Link to={item.path} key={item.label} style={{ textAlign: 'center', color: location.pathname.startsWith(item.path) ? branding.primaryColor : '#6c757d', textDecoration: 'none' }}>
              {item.icon}
              <div style={{ fontSize: 10, marginTop: 4 }}>{item.label}</div>
            </Link>
          )
        })}
      </div>
    );
};

const MobileHeader: React.FC<{ onProfileClick: () => void }> = ({ onProfileClick }) => {
    const profile = useSelector((state: RootState) => state.profile.profile);
    const unreadCount = useAppSelector(state => state.notifications.unreadCount);
    const navigate = useNavigate();
    
    const handleNotificationClick = () => {
        navigate('/notifications');
    };
    
    return (
        <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f4f6f8' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
                <h2 style={{ margin: 0, color: branding.textColor, fontWeight: 700, fontSize: '18px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {getTimeBasedGreeting(profile?.firstName)}
                </h2>
                <div style={{ color: '#888', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {branding.dashboardPage.subheader}
                </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                    title="FAQ"
                    onClick={() => navigate('/faq')}
                    style={{
                        background: 'none',
                        border: '1.5px solid currentColor',
                        borderRadius: '999px',
                        cursor: 'pointer',
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        padding: '2px 8px',
                        lineHeight: 1.4,
                        color: '#555',
                        fontFamily: 'inherit',
                    }}
                >
                    FAQ
                </button>
                <button 
                    title="Notifications" 
                    onClick={handleNotificationClick}
                    style={{ 
                        position: 'relative', 
                        background: 'none', 
                        border: 'none', 
                        cursor: 'pointer', 
                        padding: 0 
                    }}
                >
                    <FaBell size={22} color="#495057" />
                    {unreadCount > 0 && (
                        <div style={{ 
                            position: 'absolute', 
                            top: 0, 
                            right: 0, 
                            width: 7, 
                            height: 7, 
                            borderRadius: '50%', 
                            backgroundColor: branding.errorColor 
                        }} />
                    )}
                </button>
                <button onClick={onProfileClick} title="Profile" style={{
                    width: 36, height: 36, borderRadius: '50%', border: `2px solid ${branding.primaryColor}`,
                    background: '#fff', cursor: 'pointer', overflow: 'hidden',
                    boxShadow: '0 2px 6px rgba(10,132,255,0.10)', padding: 0
                }}>
                    <img src={
                        profile?.avatarBase64
                            ? `data:image/jpeg;base64,${profile.avatarBase64}`
                            : DEFAULT_HEADER_PROFILE_IMAGE
                    } alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </button>
            </div>
        </div>
    );
};

const MoreMenuModal: React.FC<{ visible: boolean; onClose: () => void; onNavigate: (path:string) => void }> = ({ visible, onClose, onNavigate }) => {
    const { getFilteredNavigation } = useAccessControl();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    
    if (!visible) return null;

    // Get filtered menu items based on account type
    const filteredNavigation = getFilteredNavigation();
    
    const iconColor = '#086afb'; // Zogzila accent blue
    const menuItems = [
        { label: 'Cards', path: '/cards', icon: <FaCreditCard size={20} color={iconColor} /> },
        { label: 'Savings', path: '/savings', icon: <FaPiggyBank size={20} color={iconColor} /> },
        { label: 'Assets', path: '/assets', icon: <FaChartPie size={20} color={iconColor} /> },
        { label: 'Crypto', path: '/crypto', icon: <FaBtc size={20} color={iconColor} /> },
        { label: 'MT5 Trading', path: '/mt5-trading', icon: <FaChartLine size={20} color={iconColor} /> },
        { label: 'Trading Bots', path: '/trading-bots', icon: <FaChartLine size={20} color={iconColor} /> },
        { label: 'Crypto Portfolio', path: '/crypto-portfolio', icon: <FaChartPie size={20} color={iconColor} /> },
        { label: 'Market signals', path: '/market', icon: <FaChartLine size={20} color={iconColor} /> },
        { label: 'iGaming', path: '/igaming', icon: <FaChartPie size={20} color={iconColor} /> },
        { label: 'P2P Games', path: '/p2p-games', icon: <FaChartLine size={20} color={iconColor} /> },
        { label: 'Social Media', path: '/social-media', icon: <FaShareAlt size={20} color={iconColor} /> },
        { label: 'AI Creator Chat', path: '/creator-chat', icon: <FaComments size={20} color={iconColor} /> },
        { label: 'Account', path: '/account', icon: <FaUserCircle size={20} color={iconColor} /> },
        { label: 'Payees', path: '/payees', icon: <FaUsers size={20} color={iconColor} /> },
        { label: 'Child Wallets', path: '/child-wallets', icon: <FaUserCircle size={20} color={iconColor} /> },
        { label: 'Notifications', path: '/notifications', icon: <FaBell size={20} color={iconColor} /> },
        { label: 'Settings', path: '/settings', icon: <FaCog size={20} color={iconColor} /> },
        { label: 'Profile', path: '/profile', icon: <FaUserCircle size={20} color={iconColor} /> },
    ].filter(item => 
        // Filter items based on allowed navigation - only show items that user has permission for
        filteredNavigation.some(navItem => navItem.path === item.path) ||
        // Always allow profile for all users (profile is typically always accessible)
        item.path === '/profile'
    );

    // Add logout as the last item
    const handleLogoutClick = () => {
        dispatch(logoutUser());
        navigate('/login');
        onClose();
    };

    return (
        <div 
            style={{ 
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
                background: 'rgba(0,0,0,0.5)', zIndex: 1100,
                display: 'flex', alignItems: 'flex-end'
            }} 
            onClick={onClose}
        >
            <style>
            {`
              @keyframes slideUp {
                from { transform: translateY(100%); }
                to { transform: translateY(0); }
              }
            `}
            </style>
            <div 
                style={{ 
                    width: '100%',
                    background: 'white', 
                    borderTopLeftRadius: 16, 
                    borderTopRightRadius: 16, 
                    maxHeight: '70vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    animation: 'slideUp 0.3s ease-out' 
                }} 
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ textAlign: 'center', color: '#ccc', padding: '12px 0 8px 0', flexShrink: 0 }}>
                    <div style={{ width: '40px', height: '4px', background: '#cbd5e1', borderRadius: '2px', margin: 'auto' }}></div>
                </div>
                <div style={{ 
                    flex: '1 1 auto',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    padding: '0 16px',
                    paddingBottom: '32px'
                }}>
                    {menuItems.map((item, index) => (
                        <div 
                            key={item.label} 
                            onClick={() => onNavigate(item.path)} 
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                padding: '16px 8px', 
                                cursor: 'pointer',
                                borderTop: index > 0 ? '1px solid #f5f5f5' : 'none'
                            }}
                        >
                            <div style={{ marginRight: '16px' }}>{item.icon}</div>
                            <div style={{ fontSize: '16px', fontWeight: 500, color: branding.textColor }}>{item.label}</div>
                        </div>
                    ))}
                    {/* Logout option */}
                    <div 
                        onClick={handleLogoutClick} 
                        style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '16px 8px', 
                            cursor: 'pointer',
                            borderTop: '1px solid #f5f5f5',
                            marginTop: '8px'
                        }}
                    >
                        <div style={{ marginRight: '16px', color: branding.errorColor }}>
                            <span style={{ display: 'inline-block', transform: 'rotate(180deg)' }}>
                                <FaUserCircle size={20} />
                            </span>
                        </div>
                        <div style={{ fontSize: '16px', fontWeight: 500, color: branding.errorColor }}>Logout</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- DESKTOP COMPONENTS ---
const Sidebar: React.FC<{ visible: boolean; onToggle: () => void }> = ({ visible, onToggle }) => {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { getFilteredNavigation } = useAccessControl();
    
    // Get filtered navigation items
    const filteredNavigation = getFilteredNavigation();
    
    // Map filtered navigation to desktop nav links format
    const desktopNavLinks = filteredNavigation.map(item => ({
        label: item.label,
        path: item.path,
        icon: getDesktopIcon(item.path)
    }));
    
    // Add logout option for all users
    desktopNavLinks.push({
        label: 'Logout',
        path: '/logout',
        icon: <span style={{ display: 'inline-block', transform: 'rotate(180deg)' }}><FaUserCircle color="#dc2626" /></span>
    });
    
    // Helper function to get desktop icons
    function getDesktopIcon(path: string): React.JSX.Element {
        const iconColor = '#086afb'; // Zogzila accent blue
        const iconMap: { [key: string]: React.JSX.Element } = {
            '/dashboard': <FaHome color={iconColor} />,
            '/transactions': <FaList color={iconColor} />,
            '/account': <FaUserCircle color={iconColor} />,
            '/payments': <FaReceipt color={iconColor} />,
            '/cards': <FaCreditCard color={iconColor} />,
            '/payees': <FaUsers color={iconColor} />,
            '/savings': <FaPiggyBank color={iconColor} />,
            '/assets': <FaChartLine color={iconColor} />,
            '/crypto': <FaBtc color={iconColor} />,
            '/mt5-trading': <FaChartLine color={iconColor} />,
            '/trading-bots': <FaChartLine color={iconColor} />,
            '/crypto-portfolio': <FaChartPie color={iconColor} />,
            '/market': <FaChartLine color={iconColor} />,
            '/social-media': <FaShareAlt color={iconColor} />,
            '/creator-chat': <FaComments color={iconColor} />,
            '/child-wallets': <FaUserCircle color={iconColor} />,
            '/notifications': <FaBell color={iconColor} />,
            '/settings': <FaCog color={iconColor} />
        };
        return iconMap[path] || <FaHome color={iconColor} />;
    }
    
    const handleNavClick = (link: typeof desktopNavLinks[0]) => {
      if (link.label === 'Logout') {
        dispatch(logoutUser());
        navigate('/login');
      } else {
        navigate(link.path);
      }
    };

    return (
        <div style={{
            width: visible ? '240px' : '90px', position: 'fixed', top: 0, left: 0, bottom: 0,
            backgroundColor: branding.sidebar.background, color: branding.sidebar.textColor, display: 'flex', flexDirection: 'column',
            padding: '24px 0', boxShadow: '2px 0 10px rgba(0,0,0,0.1)', transition: 'width 0.3s ease',
        }}>
            <div style={{ padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: visible ? 'space-between' : 'center', marginBottom: 32 }}>
                {visible && <div style={{ fontSize: branding.sidebar.logo.fontSize, fontWeight: branding.sidebar.logo.fontWeight, color: branding.sidebar.logo.color }}>{branding.sidebar.logoText}</div>}
                <button onClick={onToggle} style={{ background: 'none', border: 'none', color: '#000000', cursor: 'pointer', fontSize: 20 }}>
                    {visible ? <FaChevronLeft /> : <FaBars />}
                </button>
            </div>
            <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                {desktopNavLinks.map(link => (
                    <div key={link.label}
                      title={link.label}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '16px', textDecoration: 'none', color: branding.sidebar.textColor,
                        padding: '12px 24px', margin: '4px 8px', borderRadius: 8,
                        backgroundColor: location.pathname.startsWith(link.path) ? branding.sidebar.activeBackground : 'transparent',
                        fontWeight: location.pathname.startsWith(link.path) ? 'bold' : 'normal',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleNavClick(link)}
                    >
                      {React.cloneElement(link.icon, { size: 20, color: link.path === '/logout' ? '#dc2626' : '#086afb' })}
                      {visible && <span>{link.label}</span>}
                    </div>
                ))}
            </nav>
        </div>
    );
};

const Header: React.FC<{ sidebarVisible: boolean, onProfileClick: () => void }> = ({ sidebarVisible, onProfileClick }) => {
    const profile = useSelector((state: RootState) => state.profile.profile);
    const unreadCount = useAppSelector(state => state.notifications.unreadCount);
    const navigate = useNavigate();
    
    const handleNotificationClick = () => {
        navigate('/notifications');
    };
    
    return (
        <header style={{
            position: 'fixed', top: 0, right: 0,
            left: sidebarVisible ? '240px' : '80px',
            height: branding.header.height,
            backgroundColor: branding.header.background,
            borderBottom: `1px solid ${branding.borderColor}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 32px', zIndex: 900, transition: 'left 0.3s ease-in-out',
        }}>
            <div style={{
                width: '100%', maxWidth: 1200,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 32,
            }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <h2 style={{ margin: 0, color: branding.textColor, fontWeight: 700, fontSize: 28, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {getTimeBasedGreeting(profile?.firstName)}
                    </h2>
                    <div style={{ color: '#888', fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {branding.dashboardPage.subheader}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <button
                        title="FAQ"
                        onClick={() => navigate('/faq')}
                        style={{
                            background: 'none',
                            border: '1.5px solid currentColor',
                            borderRadius: '999px',
                            cursor: 'pointer',
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.05em',
                            padding: '2px 8px',
                            lineHeight: 1.4,
                            color: '#555',
                            fontFamily: 'inherit',
                        }}
                    >
                        FAQ
                    </button>
                    <button 
                        title="Notifications" 
                        onClick={handleNotificationClick}
                        style={{ 
                            position: 'relative', 
                            background: 'none', 
                            border: 'none', 
                            cursor: 'pointer' 
                        }}
                    >
                        <FaBell size={20} color="#495057" />
                        {unreadCount > 0 && (
                            <div style={{ 
                                position: 'absolute', 
                                top: 0, 
                                right: 0, 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%', 
                                backgroundColor: branding.errorColor 
                            }} />
                        )}
                    </button>
                    <button onClick={onProfileClick} title="Profile" style={{
                        width: 48, height: 48, borderRadius: '50%', border: `2px solid ${branding.primaryColor}`,
                        background: '#fff', cursor: 'pointer', overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(10,132,255,0.10)', padding: 0
                    }}>
                        <img src={
                            profile?.avatarBase64
                                ? `data:image/jpeg;base64,${profile.avatarBase64}`
                                : DEFAULT_HEADER_PROFILE_IMAGE
                        } alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </button>
                </div>
            </div>
        </header>
    );
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAppSelector(state => state.auth);
  const location = useLocation();
  
  // Allow registration pages without authentication
  const isRegistrationPage = location.pathname.startsWith('/register') || location.pathname === '/business/register';
  if (isRegistrationPage || location.pathname === '/verify-email') {
    return <>{children}</>;
  }
  
  if (isLoading) {
    return (
      <div className="loading-container">
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: 18, 
            color: branding.textColor, 
            marginBottom: 16,
            fontFamily: branding.fontFamily 
          }}>
            Loading...
          </div>
          <div style={{ color: '#666' }}>Please wait while we load your content</div>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// --- LAYOUT CONTROLLER ---
const AppLayout: React.FC = () => {
    const [sidebarVisible, setSidebarVisible] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
    const adminUser = useAppSelector(state => state.auth.adminUser);
    const authLoading = useAppSelector(state => state.auth.isLoading);
    const profile = useAppSelector(state => state.profile.profile);
    const profileLoading = useAppSelector(state => state.profile.isLoading);
    const profileError = useAppSelector(state => state.profile.error);
    const dispatch = useAppDispatch();

    // Hide navigation and header on login, register, business registration, and admin pages
    const isAuthPage =
        location.pathname === '/login' ||
        location.pathname.startsWith('/register') ||
        location.pathname === '/verify-email' ||
        location.pathname === '/business/register' ||
        location.pathname === '/truelayer-connect' ||
        location.pathname === '/trueLayer-cnnect';

    const isAdminPage = location.pathname.startsWith('/admin');
    const isRetailRenewStandalone = location.pathname === '/renew-subscription';

    // Public routes that don't require login (marketing home + optional /market preview + FAQ + creator chat widget)
    const isPublicLandingRoute =
        location.pathname === '/' ||
        location.pathname === '/market' ||
        location.pathname === '/faq' ||
        location.pathname.startsWith('/widget/chat/');

    // Enable token expiry checking
    useTokenExpiryCheck();
    
    // Listen for auth expired events
    useAuthExpiredListener();
    
    // NOTE: Inactivity timeout is handled in AppWithWebSocket component below
    // Don't add another instance here as it causes infinite re-render loops

    // Initialize auth on app start
    useEffect(() => {
        console.log('App.tsx: Calling initializeAuth...');
        dispatch(initializeAuth()).then((result) => {
            console.log('App.tsx: initializeAuth result:', result);
        }).catch((error) => {
            console.error('App.tsx: initializeAuth error:', error);
        });
    }, [dispatch]);

    // Initialize auth middleware for automatic refresh
    useEffect(() => {
        if (isAuthenticated) {
            // Use different intervals for admin vs regular users
            const checkInterval = location.pathname.startsWith('/admin') ? 300000 : 60000; // 5 minutes for admin, 1 minute for regular users
            
            // Start periodic token expiry checks for JWT auth
            const tokenCheckInterval = setInterval(() => {
                // Skip aggressive checks for admin pages
                if (location.pathname.startsWith('/admin')) {
                    console.log('Skipping aggressive session check for admin page');
                    return;
                }
                authMiddleware.checkTokenExpiry();
            }, checkInterval);

            return () => {
                clearInterval(tokenCheckInterval);
            };
        }
    }, [isAuthenticated]);

    // Fetch profile when authenticated and missing — skip while a fetch is already in flight (e.g. Login awaits fetchProfile)
    useEffect(() => {
        if (
            isAuthenticated &&
            !profile &&
            !profileLoading &&
            !profileError &&   // don't retry after a failed fetch (e.g. 401)
            !location.pathname.startsWith('/admin') &&
            location.pathname !== '/renew-subscription'
        ) {
            const timeoutId = setTimeout(() => {
                dispatch(fetchProfile());
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [isAuthenticated, dispatch, profile, profileLoading, profileError, location.pathname]);

    // Expired / lapsed retail subscriptions: only the renewal shell (enforced on API via SubscriptionFilter).
    useEffect(() => {
        if (
            authLoading ||
            !isAuthenticated ||
            adminUser ||
            location.pathname.startsWith('/admin') ||
            isAuthPage ||
            isPublicLandingRoute ||
            isRetailRenewStandalone
        ) {
            return;
        }
        let cancelled = false;
        void (async () => {
            try {
                const stRes = await apiService.get<{ status?: string }>('/api/retail/subscription/status');
                if (cancelled) return;
                const st = stRes.success && stRes.data ? stRes.data.status : undefined;
                if (st !== 'ACTIVE' && st !== 'GRACE_PERIOD') {
                    navigate('/renew-subscription', { replace: true });
                }
            } catch {
                /* ignore */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [
        authLoading,
        isAuthenticated,
        adminUser,
        location.pathname,
        navigate,
        isAuthPage,
        isPublicLandingRoute,
        isRetailRenewStandalone,
    ]);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
            setIsMobile(mobile);
            if (mobile) setSidebarVisible(false);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleProfileClick = () => navigate('/profile');
    const handleMoreMenuNavigate = (path: string) => {
        setShowMoreMenu(false);
        navigate(path);
    };

    // Treat both retail and admin sessions as authenticated for preview layout decisions.
    const hasPortalSession = isAuthenticated || !!adminUser;

    /** Full-bleed layout (no sidebar/header): `/` always; `/market` only when no active session (retail/admin). FAQ and creator chat widgets are always shell-free. */
    const isLandingPageNoShell =
      location.pathname === '/' ||
      location.pathname === '/faq' ||
      location.pathname.startsWith('/widget/chat/') ||
      (location.pathname === '/market' && !hasPortalSession);

    // Redirect unauthenticated users to login unless on an auth page, registration page, or public landing.
    // Admin protected routes redirect to /admin/login (not retail /login) to avoid blank screens after session expiry.
    // Do not redirect while auth is still loading: with PersistGate loading={null}, the first paint can run before
    // REHYDRATE/initializeAuth — otherwise Stripe (full-page) returns to /dashboard and briefly see isAuthenticated=false.
    const isRegistrationPage = location.pathname.startsWith('/register/') || location.pathname === '/register';
    if (
        !authLoading &&
        !isAuthenticated &&
        !isAuthPage &&
        !isRegistrationPage &&
        !isPublicLandingRoute
    ) {
        if (isAdminPage && !isAdminPublicRoute(location.pathname)) {
            return <Navigate to="/admin/login" replace />;
        }
        if (!isAdminPage) {
            return <Navigate to="/login" replace />;
        }
    }

    // Consolidated Routes component
    const AppRoutes = () => (
        <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register">
                <Route index element={<Navigate to="/login" replace />} />
                <Route path="personal" element={<PersonalRegister />} />
                <Route path="personal/enhanced" element={<EnhancedPersonalRegister />} />
                <Route path="business/enhanced" element={<EnhancedBusinessRegister />} />
                <Route path="business" element={<BusinessRegister />} />
                <Route path="*" element={<Navigate to="/register/personal" replace />} />
            </Route>
            <Route path="/business/register" element={<BusinessRegister />} />
            <Route path="/truelayer-connect" element={<TrueLayerConnect />} />
            <Route path="/trueLayer-cnnect" element={<TrueLayerConnect />} />
            <Route path="/social-connect" element={<SocialConnect />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/confirm-geolocation" element={<ConfirmLoginChallenge />} />
            <Route path="/confirm-new-device" element={<ConfirmLoginChallenge />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLayout />}>
                <Route path="login" element={<AdminLogin />} />
                <Route path="confirm-geolocation" element={<ConfirmLoginChallenge />} />
                <Route path="confirm-new-device" element={<ConfirmLoginChallenge />} />
                <Route path="subscription-renew" element={<AdminSubscriptionRenewalPage />} />
                <Route element={<AdminSubscriptionGate />}>
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="customers" element={<CustomerManagement />} />
                    <Route path="settings" element={<AdminSettings />} />
                    <Route path="trading-bots" element={<AdminTradingBots />} />
                    <Route path="kyt" element={<Navigate to="/admin/dashboard?tab=kyt" replace />} />
                </Route>
            </Route>
            
            {/* Protected Routes */}
            <Route path="/renew-subscription" element={<ProtectedRoute><RetailSubscriptionRenewalPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
            <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
            <Route path="/payments" element={<ProtectedRoute><MakePayment /></ProtectedRoute>} />
            <Route path="/cards" element={<ProtectedRoute><Cards /></ProtectedRoute>} />
            <Route path="/payees" element={<ProtectedRoute><Payees /></ProtectedRoute>} />
            <Route path="/savings" element={<ProtectedRoute><Savings /></ProtectedRoute>} />
            <Route path="/assets" element={<ProtectedRoute><Assets /></ProtectedRoute>} />
            <Route path="/crypto" element={<ProtectedRoute><Crypto /></ProtectedRoute>} />
            <Route path="/child-wallets" element={<ProtectedRoute><ChildWallet /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/batch" element={<ProtectedRoute><Batch /></ProtectedRoute>} />
            <Route path="/stablecoin" element={<ProtectedRoute><Stablecoin /></ProtectedRoute>} />
            <Route path="/mt5-trading" element={<ProtectedRoute><MT5Trading /></ProtectedRoute>} />
            <Route path="/trading-bots" element={<ProtectedRoute><TradingBots /></ProtectedRoute>} />
            <Route path="/crypto-portfolio" element={<ProtectedRoute><CryptoPortfolio /></ProtectedRoute>} />
            <Route path="/order-book" element={<ProtectedRoute><OrderBookPage /></ProtectedRoute>} />
            <Route path="/igaming" element={<ProtectedRoute><IGaming /></ProtectedRoute>} />
            <Route path="/p2p-games" element={<ProtectedRoute><P2PGames /></ProtectedRoute>} />
            <Route path="/p2p-games/challenges" element={<ProtectedRoute><PendingChallenges /></ProtectedRoute>} />
            <Route path="/p2p-game/lobby/:challengeId" element={<ProtectedRoute><GameLobby /></ProtectedRoute>} />
            <Route path="/p2p-game/play/:sessionId" element={<ProtectedRoute><P2PGamePlayer /></ProtectedRoute>} />
            <Route path="/friends" element={<ProtectedRoute><Friends /></ProtectedRoute>} />
            <Route path="/game/:gameId" element={<ProtectedRoute><GamePlayer /></ProtectedRoute>} />
            <Route path="/profit-analytics" element={<ProtectedRoute><ProfitAnalytics /></ProtectedRoute>} />
            <Route path="/social-media" element={<ProtectedRoute><SocialMedia /></ProtectedRoute>} />
            <Route path="/creator-chat" element={<ProtectedRoute><RetailCreatorChatCatalog /></ProtectedRoute>} />
            <Route path="/creator-chat/:publicId" element={<ProtectedRoute><CreatorChatWidgetPage embedded /></ProtectedRoute>} />
            <Route path="/multi-signature-wallets" element={<ProtectedRoute><MultiSignatureWallets /></ProtectedRoute>} />
            <Route path="/market" element={<MarketLanding />} />
            <Route path="/" element={<MarketLanding />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/widget/chat/:publicId" element={<CreatorChatWidgetPage />} />
        </Routes>
    );

    // Landing (see isLandingPageNoShell), admin, auth pages, and retail subscription renewal — no banking shell
    const noShell = isLandingPageNoShell || isAuthPage || isAdminPage || isRetailRenewStandalone;

    const isCreatorChatWidget = location.pathname.startsWith('/widget/chat/');

    if (isMobile) {
        return (
            <div
                style={{
                    paddingBottom: noShell ? 0 : 65,
                    ...(isCreatorChatWidget
                        ? { height: '100svh', minHeight: '100svh', overflow: 'hidden' }
                        : {}),
                }}
            >
                {!noShell && <MobileHeader onProfileClick={handleProfileClick} />}
                <PageTransition>
                    <AppRoutes />
                </PageTransition>
                {!noShell && <BottomNavBar onMoreClick={() => setShowMoreMenu(true)} />}
                {!noShell && (
                    <MoreMenuModal 
                        visible={showMoreMenu} 
                        onClose={() => setShowMoreMenu(false)} 
                        onNavigate={handleMoreMenuNavigate} 
                    />
                )}
            </div>
        );
    }

    // No-shell pages (landing, auth, admin) — render routes directly
    if (noShell) {
        return (
            <div
                style={{
                    background: isLandingPageNoShell ? '#f8fafc' : branding.pageBackground,
                    minHeight: '100vh',
                    ...(isCreatorChatWidget
                        ? { height: '100svh', minHeight: '100svh', overflow: 'hidden' }
                        : {}),
                }}
            >
                <PageTransition>
                    <AppRoutes />
                </PageTransition>
            </div>
        );
    }
    
    const collapsedWidth = '80px';
    const expandedWidth = '240px';

    return (
        <div style={{ display: 'flex', background: branding.pageBackground, minHeight: '100vh' }}>
            <Sidebar visible={sidebarVisible} onToggle={() => setSidebarVisible(!sidebarVisible)} />
            <div style={{ 
                flex: 1, 
                marginLeft: sidebarVisible ? expandedWidth : collapsedWidth, 
                transition: 'margin-left 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh'
            }}>
                <Header sidebarVisible={sidebarVisible} onProfileClick={handleProfileClick} />
                <main style={{ 
                    padding: `${branding.header.height} 32px 32px`,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <PageTransition>
                        <AppRoutes />
                    </PageTransition>
                    <Footer />
                </main>
            </div>
        </div>
    );
};

function useAuthInit() {
  const dispatch = useAppDispatch();
  useEffect(() => {
    const user = adminAuthService.getCurrentAdmin();
    if (user && user.username && user.role) {
      dispatch(setAdminUser(user));
    } else {
      dispatch(setAdminUser(null));
    }
  }, [dispatch]);
}

function AppWithWebSocket() {
  useAuthInit(); // <-- Add this line at the top  
  const location = useLocation();
  useWebSocketNotifications(!location.pathname.startsWith('/admin'));        
  useContextAwarePersistence(); // Add context-aware persistence

  // Add global error handler for MetaMask errors
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      // Check if the error is from MetaMask
      const isMetaMaskError = 
        event.error?.message?.includes('MetaMask') ||
        event.error?.message?.includes('Failed to connect to MetaMask') ||
        event.message?.includes('MetaMask') ||
        event.message?.includes('Failed to connect to MetaMask');

      // Suppress MetaMask connection errors
      if (isMetaMaskError) {
        // Log for debugging but don't show to user
        console.debug('MetaMask connection error suppressed:', event.error?.message || event.message);
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Check if the rejection is from MetaMask
      const isMetaMaskError = 
        event.reason?.message?.includes('MetaMask') ||
        event.reason?.message?.includes('Failed to connect to MetaMask') ||
        (typeof event.reason === 'string' && (
          event.reason.includes('MetaMask') ||
          event.reason.includes('Failed to connect to MetaMask')
        ));

      // Suppress MetaMask connection errors from promise rejections
      if (isMetaMaskError) {
        // Log for debugging but don't show to user
        console.debug('MetaMask connection error (promise rejection) suppressed:', 
          event.reason?.message || event.reason);
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    // Add error handlers
    window.addEventListener('error', handleError, true); // Use capture phase
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true); // Use capture phase
    
    // Cleanup
    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
    };
  }, []);

  // Get session timeout values from feature flags
  const { auth } = useFeatureFlags();
  const isAdminPage = location.pathname.startsWith('/admin');
  const timeoutMinutes = isAdminPage ? auth.adminSessionTimeoutMinutes : auth.retailSessionTimeoutMinutes;

  // Get inactivity timeout state - DISABLE for admin pages to prevent duplicate hook from AppLayout
  const { showModal, timeRemaining, handleContinue, handleLogout, debug } = useInactivityTimeout({
    timeoutMinutes,
    countdownSeconds: 120,
    testMode: process.env.NODE_ENV === 'development' && window.location.search.includes('test-timeout=true'),
    disabled: isAdminPage // ✅ FIX: Disable for admin pages (handled by AppLayout hook instead)
  });

  return (
    <>
      <SubscriptionExpiredBanner />
      <SubscriptionExpiredScreen />
      <AppLayout />
      <ToastContainer position="top-right" autoClose={5000} />
      <InactivityModal 
        isVisible={showModal}
        timeRemaining={timeRemaining}
        onContinue={handleContinue}
        onLogout={handleLogout}
      />
      {/* Feature Flag Components */}
      <TokenRefreshTest key="token-refresh-test" />
      <DebugPanel key="debug-panel" />
    </>
  );
}

const queryClient = new QueryClient();

function AppWrapper() {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <Router>
            <AppWithWebSocket />
          </Router>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
}

export default AppWrapper;
